/**
 * Pure helpers over the ProseMirror document (`031-content-format.md`). No editor, no
 * React, no side effects — so they can be unit-tested and reused from the outbox, which
 * has no editor to ask.
 */
import type { ProseMirrorDoc, ProseMirrorNode } from '@/api/endpoints/pages';

/** A `blob:` or `data:` src that got as far as the enqueue boundary. A developer error. */
export class TransientContentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TransientContentError';
  }
}

function isTransientSrc(src: unknown): boolean {
  return typeof src === 'string' && (src.startsWith('blob:') || src.startsWith('data:'));
}

function walk(node: ProseMirrorNode, visit: (n: ProseMirrorNode) => void): void {
  visit(node);
  for (const child of node.content ?? []) walk(child, visit);
}

/**
 * §8 layer 2 (`112-offline-drafts.md`) — the guard at the enqueue boundary.
 *
 * A `blob:` src must NEVER reach the server. It is meaningless in every browser session
 * but the one that created it, so persisting it writes a permanently broken image into
 * the database, forever — and `content_text` and the search index get recomputed from a
 * document containing garbage.
 *
 * Layer 1 (the save is deferred while uploads are in flight) is the primary mechanism and
 * this should therefore be unreachable. It throws anyway, loudly: if it ever fires, layer
 * 1 was bypassed by a code path nobody has written yet, and a loud failure now is worth a
 * corrupted document later.
 */
export function assertNoBlobSrc(content: unknown): void {
  const doc = content as ProseMirrorDoc | null | undefined;
  if (doc === null || doc === undefined || typeof doc !== 'object') return;

  walk(doc as ProseMirrorNode, (node) => {
    if (node.type !== 'image') return;
    const attrs = node.attrs ?? {};

    if (isTransientSrc(attrs['src'])) {
      throw new TransientContentError(
        'Refusing to enqueue a document containing an unresolved image src. ' +
          'The save must wait for the upload to resolve (112-offline-drafts.md §8).',
      );
    }
    if (attrs['uploading'] === true) {
      throw new TransientContentError(
        'Refusing to enqueue a document containing an image node still marked `uploading`.',
      );
    }
  });
}

/**
 * Produce the payload the server may see: drop the transient attrs (`031` — `uploading`,
 * `uploadId` are never persisted), and drop any image node still holding an unresolved
 * src.
 *
 * Dropping the node is the failed-upload escape hatch (`112` §8): a permanently failed
 * upload must not block the page's TEXT from ever saving again. The node stays in the
 * local draft and in the editor, with its Retry chip; the words land. Neither is lost.
 *
 * Note `uploading` is declared `rendered: false` on the node — it never reaches the DOM,
 * but it DOES appear in `editor.getJSON()`. `rendered: false` does not mean "not
 * serialized", and assuming it does is how a transient attr ends up in Postgres.
 */
export function stripTransientAttrs(doc: ProseMirrorDoc): ProseMirrorDoc {
  return clean(doc) as ProseMirrorDoc;
}

function clean(node: ProseMirrorNode): ProseMirrorNode | null {
  if (node.type === 'image') {
    const attrs = { ...(node.attrs ?? {}) };
    if (isTransientSrc(attrs['src']) || attrs['uploading'] === true) return null;
    delete attrs['uploading'];
    delete attrs['uploadId'];
    return { ...node, attrs };
  }

  if (node.content === undefined) return node;

  const content = node.content
    .map(clean)
    .filter((child): child is ProseMirrorNode => child !== null);

  return { ...node, content };
}

/**
 * Flatten a document to plain text — **for display only** (the conflict diff,
 * `112-offline-drafts.md` §7.3).
 *
 * This is NOT `content_text`. `content_text` is a server-side projection
 * (`ProseMirrorTextExtractor`) and the server ignores any the client sends. If you are
 * tempted to POST the output of this function: don't. `031-content-format.md` says so in
 * as many words, and search correctness must not depend on which version of the app a
 * stale browser tab happens to be running.
 */
export function extractText(doc: ProseMirrorDoc): string {
  const out: string[] = [];
  emit(doc as ProseMirrorNode, out);
  return out
    .join('')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const BLOCKS = new Set([
  'paragraph',
  'heading',
  'blockquote',
  'codeBlock',
  'listItem',
  'taskItem',
  'horizontalRule',
  'tableRow',
]);

function emit(node: ProseMirrorNode, out: string[]): void {
  switch (node.type) {
    case 'text':
      out.push(node.text ?? '');
      return;
    case 'hardBreak':
      out.push('\n');
      return;
    case 'wikiLink':
      // The label, so that searching for an NPC finds pages that MENTION them via a link.
      out.push(String(node.attrs?.['label'] ?? ''));
      return;
    case 'image':
      // The alt, if any. NEVER the src — indexing URLs produces garbage hits (`031`).
      out.push(String(node.attrs?.['alt'] ?? ''));
      return;
    default:
      break;
  }

  const children = node.content ?? [];
  for (const [i, child] of children.entries()) {
    emit(child, out);
    // Cells are tab-separated, rows newline-separated (`031`).
    if (node.type === 'tableRow' && i < children.length - 1) out.push('\t');
  }

  if (BLOCKS.has(node.type)) out.push('\n');
}

/** Structural equality over ProseMirror JSON — reconciliation's "is this the same doc". */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null) return false;
  if (typeof a !== 'object') return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, b[i]));
  }

  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left);
  if (keys.length !== Object.keys(right).length) return false;

  // Key ORDER is not compared: `JSON.stringify` equality would be, and is fragile across
  // serializations. That fragility is exactly why `112` §7.2 asks for a real deep equal.
  return keys.every((key) => key in right && deepEqual(left[key], right[key]));
}
